import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Popper,
  Paper,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import './AutocompleteField.css';

export interface AutocompleteOption {
  value: string;
  label: string;
  productId?: string;
  item_code?: string;
  item_name?: string;
  rate?: number;
  gst_percentage?: number;
  isNew?: boolean;
}

export interface AutocompleteFieldProps {
  field: {
    name: string;
    label: string;
    placeholder: string;
    required?: boolean;
  };
  options: AutocompleteOption[];
  value: string;
  onChange: (value: string, option?: AutocompleteOption) => void;
  onOptionSelect?: (option: AutocompleteOption) => void;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  disabled?: boolean;
  error?: string;
  loading?: boolean;
}

const filter = createFilterOptions<AutocompleteOption>();

const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  field,
  options,
  value,
  onChange,
  onOptionSelect,
  size = 'small',
  variant = 'outlined',
  disabled = false,
  error,
  loading = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    onChange(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: AutocompleteOption | string | null) => {
    if (typeof newValue === 'string') {
      // User typed something new
      onChange(newValue);
      if (onOptionSelect) {
        onOptionSelect({
          value: newValue,
          label: newValue,
          isNew: true,
        });
      }
    } else if (newValue && newValue.value) {
      // User selected from dropdown
      onChange(newValue.value, newValue);
      if (onOptionSelect) {
        onOptionSelect(newValue);
      }
    }
  };

  const handleBlur = () => {
    // Keep the dropdown open briefly to allow for selection
    setTimeout(() => setOpen(false), 150);
  };

  const renderOption = (props: any, option: AutocompleteOption) => {
    const { key, ...otherProps } = props;
    return (
      <ListItem {...otherProps} key={option.value}>
        <ListItemText
          primary={option.label}
          secondary={
            option.isNew 
              ? `Create "${option.value}"` 
              : option.productId 
                ? `Existing product` 
                : undefined
          }
        />
        {option.isNew && (
          <Chip 
            label="NEW" 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </ListItem>
    );
  };

  const renderInput = (params: any) => (
    <TextField
      {...params}
      ref={inputRef}
      placeholder={field.placeholder}
      variant={variant}
      size={size}
      error={!!error}
      helperText={error}
      disabled={disabled}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {loading ? <CircularProgress color="inherit" size={20} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
      sx={{
        '& .MuiInputBase-root': {
          fontSize: '0.875rem',
        },
        '& .MuiInputBase-input': {
          padding: '8px 12px',
          minHeight: 'auto',
        },
      }}
    />
  );

  return (
    <Box className="autocomplete-field-container">
      <Autocomplete
        value={value}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onBlur={handleBlur}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        open={open}
        options={options}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;
          
          // Add "create new" option if input doesn't match any existing option
          const isExisting = options.some((option) => 
            option.value.toLowerCase() === inputValue.toLowerCase()
          );
          
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              value: inputValue,
              label: inputValue,
              isNew: true,
            });
          }
          
          return filtered;
        }}
        getOptionLabel={(option) => {
          // For value selected with mouse, return the label
          if (typeof option === 'string') {
            return option;
          }
          return option.label;
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof value === 'string') {
            return option.value === value;
          }
          return option.value === value.value;
        }}
        renderOption={renderOption}
        renderInput={renderInput}
        freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        disabled={disabled}
        loading={loading}
        PopperComponent={(props) => (
          <Popper
            {...props}
            placement="bottom-start"
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
            ]}
          />
        )}
        PaperComponent={(props) => (
          <Paper
            {...props}
            elevation={3}
            sx={{
              maxHeight: 200,
              overflow: 'auto',
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-input': {
            padding: '8px 12px !important',
          },
        }}
      />
    </Box>
  );
};

export default AutocompleteField; 